import React from 'react';
import { CL_TOKENS, FILTER_CHIPS_BY_TAB } from '../../receptionist-call-logs/config';
import type { CallLogsTabId, CallLogsTimeRange } from '../../receptionist-call-logs';
import { Icon } from '../Icon/Icon';

const TIME_RANGES: { id: CallLogsTimeRange; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: '7days', label: 'Last 7 days' },
  { id: '30days', label: 'Last 30 days' },
  { id: 'custom', label: 'Custom' },
];

function TimeRangeToggle({
  value,
  onChange,
}: {
  value: CallLogsTimeRange;
  onChange: (v: CallLogsTimeRange) => void;
}) {
  return (
    <div
      className="inline-flex gap-px rounded-[9px] border border-[var(--cream-2)] p-0.5"
      style={{ background: CL_TOKENS.surfaceMute }}
    >
      {TIME_RANGES.map((s) => {
        const isActive = value === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onChange(s.id)}
            className="rounded-[7px] border-0 px-3 py-[5px] text-[12px] tracking-[-0.005em] transition-[background,color,box-shadow] duration-200"
            style={{
              background: isActive ? 'var(--cream-1)' : 'transparent',
              color: isActive ? 'var(--ink-0)' : CL_TOKENS.ink2,
              fontWeight: isActive ? 600 : 500,
              boxShadow: isActive ? CL_TOKENS.shadowXs : 'none',
              cursor: 'pointer',
            }}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

function FilterChip({ label }: { label: string }) {
  return (
    <button
      type="button"
      className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-[var(--cream-2)] bg-[var(--cream-1)] px-[11px] text-[12.5px] font-medium tracking-[-0.005em] shadow-[var(--sh-1)]"
      style={{ color: CL_TOKENS.ink2 }}
    >
      <span className="text-[11px] font-semibold" style={{ color: CL_TOKENS.ink4 }}>
        {label}:
      </span>
      <span className="font-medium">All</span>
      <Icon kind="down" size={11} color={CL_TOKENS.ink4} stroke={2} />
    </button>
  );
}

export interface CallLogsFilterBarProps {
  activeTab: CallLogsTabId;
  timeRange: CallLogsTimeRange;
  onTimeRangeChange: (v: CallLogsTimeRange) => void;
  search: string;
  onSearchChange: (v: string) => void;
}

export function CallLogsFilterBar({
  activeTab,
  timeRange,
  onTimeRangeChange,
  search,
  onSearchChange,
}: CallLogsFilterBarProps) {
  const chips = FILTER_CHIPS_BY_TAB[activeTab];
  return (
    <div className="cl-fade-up cl-d-2 mb-3 flex flex-wrap items-center gap-2.5">
      <TimeRangeToggle value={timeRange} onChange={onTimeRangeChange} />
      <div className="h-[22px] w-px" style={{ background: CL_TOKENS.borderMed }} />
      {chips.map((c) => (
        <FilterChip key={c} label={c} />
      ))}
      <div className="flex-1" />
      <div className="relative flex-none">
        <Icon
          kind="search"
          size={13}
          color={CL_TOKENS.ink4}
          stroke={2}
          className="pointer-events-none absolute left-[11px] top-1/2 -translate-y-1/2"
        />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search patient or phone…"
          className="h-8 w-[240px] rounded-lg border border-[var(--cream-2)] bg-[var(--cream-1)] py-0 pl-8 pr-[11px] text-[12.5px] tracking-[-0.005em] text-[var(--ink-0)] shadow-[var(--sh-1)] outline-none"
        />
      </div>
      <button
        type="button"
        className="inline-flex h-[30px] items-center gap-1.5 rounded-[7px] border-0 bg-transparent px-2.5 text-[12px] font-medium"
        style={{ color: CL_TOKENS.ink2, cursor: 'pointer' }}
      >
        <Icon kind="download" size={12} stroke={2} />
        Export
      </button>
    </div>
  );
}
